import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { GrowthService, type GrowthCategory } from '@mc/api'
import { useStaticData } from '@mc/static-data-react'

import type { getCategories, getV2Categories } from 'libs/server/api'

const staleTime = 1000 * 60 * 60 // 1 hour

export const useGrowthCategories = () => {
  const serverCategories = useSSRPlaceholderGrowthCategories()
  return useQuery({
    queryKey: ['growth-categories'],
    queryFn: () => GrowthService.getGrowthCategories(),
    // the SSR categories are parent cats only, so they're only good for placeholder data.
    // but, if they are available, having them populated immediately will let us render
    // the first menu (parent cats) of the navbar Browse component instantly.
    placeholderData: serverCategories,
    staleTime,
  })
}

const useSSRPlaceholderGrowthCategories = () => {
  const { categories: serverCategories } = useStaticData<{
    categories?: Awaited<
      ReturnType<typeof getV2Categories | typeof getCategories>
    >
  }>()

  if (!serverCategories) return undefined
  const serverCategory = serverCategories?.[0]
  // we only want to return the placeholder categories if they are GrowthCategory objects
  // otherwise, don't use them
  if (serverCategory && 'uuid' in serverCategory) {
    return serverCategories as GrowthCategory[]
  }
  return undefined
}

export const useGrowthContent = () =>
  useQuery({
    queryKey: ['growth-content'],
    queryFn: () => GrowthService.getGrowthContent(),
    staleTime,
  })

export const useGrowthCategoriesAndSubcategories = () => {
  const { data, isLoading, isError } = useGrowthCategories()

  const { categories, subcategories } = useMemo(() => {
    if (!data) {
      return { categories: [], subcategories: [] }
    }

    return data.reduce(
      (acc, category) => {
        if (category.parentId === undefined || category.parentId === null) {
          acc.categories.push(category)
        } else {
          acc.subcategories.push(category)
        }
        return acc
      },
      {
        categories: [] as GrowthCategory[],
        subcategories: [] as GrowthCategory[],
      },
    )
  }, [data])

  return { categories, subcategories, isLoading, isError }
}
